import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { upperFirst } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DroverEvents, Vehicle_TypeEnum } from 'src/api/gen'
import { DEVICES_ICONS } from 'src/components/Devices'

export interface IClickedDroverPoint {
  coordinates: mapboxgl.LngLat
  drover: DroverEvents
}

export function timestamp_to_local_string(timestamp: string, format?: string) {
  const local = dayjs(timestamp).local()
  if (format) {
    return local.format(format).toString()
  }
  return local.toString()
}

interface IDroverPopupProps extends WithStyles<typeof styles> {
  droverEvent: DroverEvents
}

function DroverPopup(props: IDroverPopupProps) {
  const { classes, droverEvent } = props

  const { t } = useTranslation()

  const vehicle_type =
    droverEvent?.vehicle_type in Vehicle_TypeEnum ? (droverEvent.vehicle_type as Vehicle_TypeEnum) : undefined
  const providerImage = `url(${vehicle_type ? DEVICES_ICONS[vehicle_type] : '/img/img-placeholder.svg'})`

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <div style={{ backgroundImage: providerImage }} className={classes.media} />
      </div>
      <div className={classes.vertLine} />
      <div style={{ width: '100%' }}>
        <div className={classes.row}>
          <div className={classes.header}>
            <div className={classNames(classes.row, classes.title)}>{upperFirst(droverEvent.provider_name)}</div>
            <div className={classes.vin}>
              <a href={`/vehicles/${droverEvent.device_id}`}>{upperFirst(droverEvent.vehicle_id)}</a>
            </div>
            <div className={classes.content}>{upperFirst(t('locationType.' + droverEvent.location_type))}</div>
            <div className={classes.content}>{droverEvent.id}</div>
            <div className={classes.content}>
              <AccessTimeIcon className={classes.icon} />
              {timestamp_to_local_string(droverEvent.timestamp, 'YYYY-MM-DD - HH:mm:ss')}
            </div>
            <div className={classes.row} style={{ height: 5 }} />
          </div>
        </div>
        <div className={classes.row} style={{ height: 10 }} />
        <div className={classes.row}></div>
      </div>
    </div>
  )
}

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '4px',
    },
    media: {
      textAlign: 'center',
      minHeight: 80,
      minWidth: 80,
      marginLeft: theme.spacing(),
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    header: {
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '20px',
      lineHeight: '30px',
      color: theme.palette.grey[800],
      cursor: 'pointer',
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column',
    },
    title: {
      fontSize: '18px',
      lineHeight: '20px',
      fontWeight: 'bold',
    },
    vin: {
      fontSize: 15,
      color: theme.palette.grey[500],
      fontWeight: 'bold',
      textDecoration: 'underline',
    },
    content: {
      fontSize: 15,
      color: theme.palette.grey[800],
      display: 'flex',
      verticalAlign: 'text-bottom',
    },
    icon: {
      marginRight: theme.spacing(),
      marginTop: 2,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    vertLine: {
      height: 100,
      width: 2,
      background: '#e8e7e7',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  })

export default withStyles(styles)(DroverPopup)
